import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarClock,
  Circle,
  CircleDot,
  Eye,
  Lock,
  CornerDownRight,
  CornerLeftUp,
  MessageSquare,
  MoreHorizontal,
  CirclePlus,
  History,
  Paperclip,
  Play,
  Square,
  Trash2,
} from 'lucide-react';

import { Avatar } from '@/components/ui/avatar';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
} from '@/components/ui/menu';
import { MoveMenuItems } from '@/components/tasks/MoveActions';
import { PriorityDot } from '@/components/tasks/PriorityDot';
import { PRIORITY_LABELS, PRIORITY_ORDER } from '@/lib/priority';
import type { OpenStage } from '@/lib/api/types';
import { LabelChip } from '@/components/tasks/LabelChip';
import { SubtaskProgress } from '@/components/tasks/Subtasks';
import { TaskCheckbox } from '@/components/tasks/TaskCheckbox';
import type { ScheduleTarget, TaskPriority, TaskSummary } from '@/lib/api/types';
import { formatDayShort, formatOverdueAge } from '@/lib/dates';
import { billingApi } from '@/lib/api/endpoints';
import { cn } from '@/lib/utils';
import { usePendingCompletion } from '@/hooks/useTasks';
import { useT, type MessageKey } from '@/lib/i18n';

export interface TaskRowProps {
  task: TaskSummary;
  today: string;
  currentUserId: string;
  onOpen: (taskId: string) => void;
  onToggleComplete: (task: TaskSummary) => void;
  onConfirmComplete: (task: TaskSummary) => void;
  onMove: (taskId: string, target: ScheduleTarget, date?: string) => void;
  onSetPriority: (taskId: string, priority: TaskPriority) => void;
  onSetStage: (taskId: string, status: OpenStage) => void;
  onDelete: (taskId: string) => void;
  /** Show the scheduled day inline — useful outside the Today screen. */
  showDate?: boolean;
  /** Render the date as "how late", for the overdue band. */
  overdue?: boolean;
}

/**
 * One task.
 *
 * The whole row opens the detail; the tick box and the menu stop propagation so
 * they act without also navigating. Everything reachable by mouse is reachable
 * by keyboard.
 */
export function TaskRow({
  task,
  today,
  currentUserId,
  onOpen,
  onToggleComplete,
  onConfirmComplete,
  onMove,
  onSetPriority,
  onSetStage,
  onDelete,
  showDate = false,
  overdue = false,
}: TaskRowProps) {
  const t = useT();
  const completed = task.status === 'COMPLETED';
  // Ticked, struck through, waiting for "return" or "finish". Client-side
  // only: nothing has been sent yet, so a lost pending tick fails safe — the
  // task stays open.
  const pending = usePendingCompletion(task.id) && !completed;
  const isMine = task.assigned_to_user_id === currentUserId;
  // Show a face only when it tells you something: who else is involved.
  const counterpart = task.is_delegated
    ? isMine
      ? task.creator
      : task.assignee
    : null;

  // Whether the informational cluster has anything in it at all.
  const hasMeta =
    task.subtask_total > 0 ||
    task.postponed_count > 0 ||
    task.attachment_count > 0 ||
    task.comment_count > 0 ||
    Boolean(task.scheduled_date && (showDate || overdue)) ||
    Boolean(counterpart);

  return (
    <div
      className={cn(
        // The white card from the macOS design, not a flat hover row.
        'group flex items-center gap-3 rounded-xl px-4 py-3 transition-surface',
        // Phone: taller rows, a 15px title, and no hover-revealed controls —
        // there is no hover. Everything that was `opacity-0` until the pointer
        // arrived was simply invisible on a touch screen.
        'max-sm:min-h-16 max-sm:px-3 max-sm:py-3.5 touch:min-h-16 touch:px-3 touch:py-3.5',
        // Wrapping is what lets the counts and the due date drop to a second
        // line on a phone. Without it they hold their width and the title is
        // truncated to "Reproduk…" — the one part of the row that must stay
        // readable is the one that loses.
        'max-sm:flex-wrap max-sm:gap-y-1.5 touch:flex-wrap touch:gap-y-1.5',
        pending
          ? 'bg-overdue-subtle ring-1 ring-overdue-border'
          : 'bg-surface shadow-xs ring-1 ring-border/60 hover:ring-accent/40',
        completed ? 'opacity-70' : '',
      )}
    >
      {task.close_protected && !task.close_armed && !completed ? (
        // No checkbox at all. A project is not closed from a list, and a
        // control that will refuse is worse than none — the lock says the
        // decision lives in the detail.
        <span
          className="flex h-5 w-5 shrink-0 items-center justify-center"
          title={t('task.protected.hint')}
        >
          <Lock className="h-3.5 w-3.5 text-content-subtle" aria-hidden />
        </span>
      ) : !task.can_act ? (
        // Read-only: you took part, but the task is somebody else's now.
        // Offering a checkbox here means offering a red toast.
        <span
          className="flex h-5 w-5 shrink-0 items-center justify-center"
          title={t('task.readOnly')}
        >
          <Eye className="h-3.5 w-3.5 text-content-subtle" aria-hidden />
        </span>
      ) : (
        <TaskCheckbox
          checked={completed || pending}
          onToggle={() => onToggleComplete(task)}
          label={task.title}
          disabled={!completed && !pending && task.blocked_by_subtasks}
          title={
            !completed && task.blocked_by_subtasks
              ? `${t('task.blockedBySubtasks')} (${task.subtask_done}/${task.subtask_total})`
              : undefined
          }
        />
      )}

      <PriorityDot priority={task.priority} />

      <button
        type="button"
        onClick={() => onOpen(task.id)}
        className="min-w-0 flex-1 text-left"
      >
        {/* The title owns the first line on a phone: sharing it with the
            labels left "Reprodukovat 409 lokálně" rendered as "Reproduk…",
            which is not a task anyone can recognise. From `sm` up there is
            room for both on one line, and the tighter layout reads better. */}
        <span className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
          {/* Project first, then the task. Behind the title it was the part
              the truncation ate, so the one row that answered "for whom" lost
              the answer; in front it is a fixed, short prefix the eye skips
              when it is the same for a whole run of rows. */}
          <span className="flex min-w-0 items-center gap-1.5">
            {task.project_name ? (
              <span
                className="flex shrink-0 items-center gap-1 text-xs text-content-muted"
                title={
                  task.project_inherited
                    ? t('task.projectInherited', { name: task.project_name })
                    : task.project_name
                }
              >
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{
                    backgroundColor: `var(--color-label-${task.project_color})`,
                  }}
                  aria-hidden
                />
                {clip(task.project_name)}
                <span className="text-content-subtle" aria-hidden>
                  ·
                </span>
              </span>
            ) : null}
            <span
              className={cn(
                // Desktop truncates — there is a wide detail pane one click
                // away. A phone wraps to two lines instead: with thumb-sized
                // controls beside it, "Zkontrolovat …" was all that survived
                // of the one part of the row that has to stay readable.
                'truncate text-sm max-sm:whitespace-normal max-sm:line-clamp-2 max-sm:text-[15px] touch:whitespace-normal touch:line-clamp-2 touch:text-[15px]',
                completed || pending
                  ? 'text-content-subtle line-through'
                  : 'text-content',
              )}
            >
              {task.title}
            </span>
          </span>
          {task.labels.length > 0 ? (
            <span className="flex shrink-0 items-center gap-1">
              {task.labels.slice(0, 3).map((label) => (
                <LabelChip key={label.id} label={label} />
              ))}
              {task.labels.length > 3 ? (
                <span className="text-[11px] text-content-subtle">
                  +{task.labels.length - 3}
                </span>
              ) : null}
            </span>
          ) : null}
        </span>

        {/* A step given a date of its own stands on Today in its own right.
            "Doplnit fotky" means nothing alone, so its parent is named — under
            the title rather than in front of it, which would push every such
            row's title out of line with the others. */}
        {task.parent_title ? (
          <span className="mt-0.5 flex min-w-0 items-center gap-1 text-xs text-content-subtle">
            <CornerDownRight className="h-3 w-3 shrink-0" aria-hidden />
            <span className="truncate">
              {t('task.stepOf', { title: task.parent_title })}
            </span>
          </span>
        ) : null}
      </button>

      {pending ? (
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            onClick={() => onToggleComplete(task)}
            className={cn(
              'rounded border border-border bg-surface px-2 py-1 text-xs font-medium',
              'text-content transition-surface hover:bg-surface-hover',
            )}
          >
            {t('complete.return')}
          </button>
          <button
            type="button"
            onClick={() => onConfirmComplete(task)}
            className={cn(
              'rounded bg-accent px-2 py-1 text-xs font-medium text-white',
              'transition-surface hover:bg-accent-hover',
            )}
          >
            {t('complete.confirm')}
          </button>
        </div>
      ) : null}

      {hasMeta ? (
        <div
          className={cn(
            'flex shrink-0 items-center gap-2 text-content-subtle',
            // Its own line below the title on a phone, lined up with the title
            // rather than with the checkbox. Rendered only when it carries
            // something: an empty second line made every dateless backlog row
            // half again as tall for nothing.
            // `order-last` keeps the row's menu up on the first line with the
            // title; without it the menu wrapped below this block and left a
            // third, empty line under every card.
            'max-sm:w-full max-sm:order-last max-sm:basis-full max-sm:pl-8 touch:w-full touch:order-last touch:basis-full touch:pl-8',
          )}
        >
          <SubtaskProgress
            done={task.subtask_done}
            total={task.subtask_total}
            blocked={task.blocked_by_subtasks}
          />

          {/* A habit of postponing, worn on the row. Amber while it is a
              nudge, red once it is a pattern — the count is only useful to
              see *before* reaching for "postpone" again. */}
          {task.postponed_count > 0 && task.status !== 'COMPLETED' ? (
            <span
              className={cn(
                'flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-medium tabular-nums',
                task.postponed_count >= 3
                  ? 'bg-danger-subtle text-danger'
                  : 'bg-priority-high-subtle text-priority-high',
              )}
              title={t('task.postponed.count', { count: task.postponed_count })}
            >
              <History className="h-3 w-3" aria-hidden />
              {task.postponed_count}×
            </span>
          ) : null}

          {task.attachment_count > 0 ? (
            <span
              className="flex items-center gap-0.5 text-xs"
              title={t('attachment.count', { count: task.attachment_count })}
            >
              <Paperclip className="h-3 w-3" aria-hidden />
              {task.attachment_count}
            </span>
          ) : null}

          {task.comment_count > 0 ? (
            <span
              className="flex items-center gap-0.5 text-xs"
              title={t('comment.count', { count: task.comment_count })}
            >
              <MessageSquare className="h-3 w-3" aria-hidden />
              {task.comment_count}
            </span>
          ) : null}

          {task.scheduled_date && (showDate || overdue) ? (
            <span
              className={cn(
                'flex items-center gap-1 text-xs tabular-nums',
                overdue ? 'font-medium text-overdue' : 'text-content-subtle',
              )}
            >
              <CalendarClock className="h-3 w-3" aria-hidden />
              {overdue
                ? formatOverdueAge(task.scheduled_date, today)
                : formatDayShort(task.scheduled_date)}
            </span>
          ) : null}

          {counterpart ? (
            <Avatar
              user={counterpart}
              size="sm"
              className={isMine ? '' : 'ring-1 ring-accent/40'}
            />
          ) : null}
        </div>
      ) : null}

      {/* The stage, beside the other things you do to a row rather than in the
          middle of it — placed between the tick box and the title it pushed
          every title sideways and read as noise down a list.

          It began in the row menu, which meant opening Today looked exactly as
          it always had: a control nobody can see is a control nobody has. In
          progress states itself, because that is the fact worth carrying down
          a list; a task not started offers to start, quietly until you reach
          for it — and always on a phone, which has no hover to reveal it. */}
      {!completed && task.can_act ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onSetStage(task.id, task.status === 'IN_PROGRESS' ? 'OPEN' : 'IN_PROGRESS');
          }}
          title={t(
            task.status === 'IN_PROGRESS' ? 'task.stopProgress' : 'task.startProgress',
          )}
          className={cn(
            'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium transition-surface',
            'max-sm:min-h-8 touch:min-h-8',
            task.status === 'IN_PROGRESS'
              ? 'bg-accent/10 text-accent hover:bg-accent/20'
              : 'text-content-subtle opacity-0 hover:bg-surface-hover hover:text-content group-hover:opacity-100 focus-visible:opacity-100 max-sm:opacity-100 touch:opacity-100',
          )}
        >
          {task.status === 'IN_PROGRESS'
            ? t('board.stage.IN_PROGRESS')
            : t('task.startProgress')}
        </button>
      ) : null}

      {/* Outside the wrapping block on purpose: the row's menu belongs beside
          the title, not alone on a line of its own. */}
      {task.status !== 'COMPLETED' && task.can_act ? <RowTimer task={task} /> : null}
      {/* The menu is moves, priorities and deletion — all refused for a task
          you only took part in. Opening it is still possible: that is what
          the row is for. */}
      {task.can_act ? (
        <RowMenu
          onSetStage={onSetStage}
          task={task}
          today={today}
          onMove={onMove}
          onSetPriority={onSetPriority}
          onDelete={onDelete}
          onOpen={onOpen}
        />
      ) : null}
    </div>
  );
}

function RowMenu({
  task,
  today,
  onMove,
  onSetPriority,
  onSetStage,
  onDelete,
  onOpen,
}: {
  task: TaskSummary;
  today: string;
  onMove: TaskRowProps['onMove'];
  onSetPriority: TaskRowProps['onSetPriority'];
  onSetStage: TaskRowProps['onSetStage'];
  onDelete: TaskRowProps['onDelete'];
  onOpen: TaskRowProps['onOpen'];
}) {
  const t = useT();
  return (
    <Menu>
      <MenuTrigger
        aria-label={t('task.actionsFor', { title: task.title })}
        className={cn(
          'rounded p-1 text-content-subtle transition-surface',
          'hover:bg-surface-raised hover:text-content',
          // Hidden until hover or keyboard focus, so rows stay quiet — but
          // never hidden from the keyboard, and never from a thumb: a touch
          // screen has no hover, so below `sm` it is always there and 44px.
          'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
          'max-sm:opacity-100 max-sm:p-2.5 touch:opacity-100 touch:p-2.5',
          'data-[state=open]:opacity-100',
        )}
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden />
      </MenuTrigger>

      <MenuContent align="end">
        {/* The way up. A step's own row names its parent but cannot link to
            it — the title is already a button — so the menu carries it. */}
        {task.parent_task_id ? (
          <>
            <MenuItem onSelect={() => onOpen(task.parent_task_id!)}>
              <CornerLeftUp className="h-3.5 w-3.5 text-content-subtle" aria-hidden />
              {t('task.openParent')}
            </MenuItem>
            <MenuSeparator />
          </>
        ) : null}
        <MenuLabel>{t('task.move')}</MenuLabel>
        <MoveMenuItems
          defaultDate={today}
          onMove={(target, date) => onMove(task.id, target, date)}
        />

        {/* Not on a finished task: the way back from done is the tick box,
            which reopens it, and offering a stage here would be a second
            answer to the same question. */}
        {task.status !== 'COMPLETED' ? (
          <>
            <MenuSeparator />
            <MenuLabel>{t('board.title')}</MenuLabel>
            {(['OPEN', 'IN_PROGRESS'] as OpenStage[]).map((stage) => (
              <MenuItem
                key={stage}
                onSelect={() => onSetStage(task.id, stage)}
                className={stage === task.status ? 'bg-surface-hover' : ''}
              >
                {stage === 'IN_PROGRESS' ? (
                  <CircleDot className="h-3.5 w-3.5" aria-hidden />
                ) : (
                  <Circle className="h-3.5 w-3.5" aria-hidden />
                )}
                {t(`board.stage.${stage}` as MessageKey)}
              </MenuItem>
            ))}
          </>
        ) : null}

        <MenuSeparator />
        <MenuLabel>{t('priority.label')}</MenuLabel>
        {PRIORITY_ORDER.map((priority) => (
          <MenuItem
            key={priority}
            onSelect={() => onSetPriority(task.id, priority)}
            className={priority === task.priority ? 'bg-surface-hover' : ''}
          >
            <PriorityDot priority={priority} />
            {t(PRIORITY_LABELS[priority])}
          </MenuItem>
        ))}

        <MenuSeparator />
        <MenuItem
          onSelect={() => onDelete(task.id)}
          className="text-danger data-highlighted:bg-danger-subtle"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
          {t('common.delete')}
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}

/**
 * The clock, one click from any list. Hidden until hover like the row menu —
 * but when this task's clock is running, it stays visible and pulses, so a
 * forgotten timer cannot hide.
 */
function RowTimer({ task }: { task: TaskSummary }) {
  const t = useT();
  const queryClient = useQueryClient();
  const { data: timer } = useQuery({
    queryKey: ['timer'],
    queryFn: () => billingApi.timer(),
    staleTime: 30_000,
  });
  const running = timer?.entries ?? [];
  const runningHere = running.some((item) => item.entry.task_id === task.id);
  const runningElsewhere = running.length > 0 && !runningHere;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['timer'] });
    void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
  };
  const start = useMutation({
    mutationFn: (parallel: boolean) => billingApi.startTimer(task.id, parallel),
    onSuccess: refresh,
  });
  const stop = useMutation({
    mutationFn: () => billingApi.stopTimer(task.id),
    onSuccess: refresh,
  });

  return (
    <>
      {/* Beside the play, only when another clock already runs: add THIS
          clock without stopping that one — the deliberate two-client moment
          gets its own button instead of a dialog in everyone else's way. */}
      {runningElsewhere ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            start.mutate(true);
          }}
          aria-label={t('time.startParallelFor', { title: task.title })}
          title={t('time.startParallel')}
          className="rounded p-1 max-sm:p-2.5 text-content-subtle opacity-0 max-sm:opacity-100 transition-surface hover:bg-surface-raised hover:text-accent group-hover:opacity-100 focus-visible:opacity-100 touch:p-2.5 touch:opacity-100"
        >
          <CirclePlus className="h-4 w-4" aria-hidden />
        </button>
      ) : null}
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          if (runningHere) stop.mutate();
          else start.mutate(false);
        }}
        aria-label={
          runningHere ? t('time.stop') : t('time.startFor', { title: task.title })
        }
        title={runningHere ? t('time.stop') : t('time.start')}
        className={cn(
          'rounded p-1 transition-surface hover:bg-surface-raised max-sm:p-2.5 touch:p-2.5',
          runningHere
            ? 'text-accent opacity-100'
            : 'text-content-subtle opacity-0 hover:text-accent group-hover:opacity-100 focus-visible:opacity-100 max-sm:opacity-100 touch:opacity-100',
        )}
      >
        {runningHere ? (
          <span className="relative flex h-4 w-4 items-center justify-center">
            <span className="absolute h-3.5 w-3.5 animate-ping rounded-full bg-accent/40" />
            <Square className="relative h-3 w-3 fill-current" aria-hidden />
          </span>
        ) : (
          <Play className="h-4 w-4" aria-hidden />
        )}
      </button>
    </>
  );
}

/// A project name is a prefix, not the content of the row: past ~20 characters
/// it starts eating the title it is supposed to introduce.
function clip(name: string): string {
  return name.length > 20 ? `${name.slice(0, 20).trimEnd()}…` : name;
}
